/**
 * @fileoverview Loads the student's final self-assessment and exposes submit.
 * Backend contract: GET /projects/:id/final_assessments.json → PATCH
 * /final_assessment_scores/:id per score (student-permitted) (+ PATCH
 * /journals/:id).
 */

import { useCallback, useState } from 'react'
import { dataSource } from '../dataSource'
import type { FinalAssessmentDetail, ScoreInput } from '../types'
import { useTaskDetail } from './useTaskDetail'

interface UseFinalAssessmentResult {
  loading: boolean
  errored: boolean
  submitting: boolean
  data: FinalAssessmentDetail | null
  submit: (scores: readonly ScoreInput[], journal: string) => Promise<boolean>
  /** Silent per-change save (autosave); no reload, no toast. */
  autosave: (scores: readonly ScoreInput[], journal: string) => Promise<boolean>
}

export function useFinalAssessment(
  identifierId: string,
  opened: boolean,
): UseFinalAssessmentResult {
  const { loading, errored, data } = useTaskDetail(
    dataSource.fetchFinalAssessment,
    identifierId,
    opened,
  )
  const [submitting, setSubmitting] = useState(false)

  const submit = useCallback(
    async (scores: readonly ScoreInput[], journal: string): Promise<boolean> => {
      if (!data?.finalAssessmentId) return false
      setSubmitting(true)
      try {
        await dataSource.submitFinalAssessment(data, scores, journal)
        // No reload(): local state already reflects the submitted values and the
        // cache is invalidated in realSource, so reopening refetches fresh. This
        // avoids a brief loading/error flash while the drawer stays open.
        return true
      } catch (error) {
        console.error('Error submitting final assessment:', error)
        return false
      } finally {
        setSubmitting(false)
      }
    },
    [data],
  )

  const autosave = useCallback(
    async (scores: readonly ScoreInput[], journal: string): Promise<boolean> => {
      if (!data?.finalAssessmentId) return false
      try {
        await dataSource.submitFinalAssessment(data, scores, journal)
        return true
      } catch (error) {
        console.error('Error autosaving final assessment:', error)
        return false
      }
    },
    [data],
  )

  return { loading, errored, submitting, data, submit, autosave }
}
